import {type SDKOptions} from './sdk-core';
import {hideCheckoutSkeleton, showCheckoutSkeleton} from './checkout-skeleton';

/**
 * Internal private API for controlling the Express Checkout iframe
 */
type ExpressCheckoutAPI = {
	show(): number;
	preload(): void;
	open(options?: CheckoutOpenOptions): Promise<void>;
	hide(): void;
	maybeMount(options?: MountOptions): Promise<void>;
	isOpenAttemptActive(openAttemptId: number): boolean;
	onClose(callback: (event: CloseFlags) => void): void;
};

type CheckoutOpenOptions = {
	needsCartRefresh?: boolean;
	openAttemptId?: number;
};

type CheckoutOpenContext = CheckoutOpenOptions & {
	iframeMountedForCurrentOpen?: boolean;
	abortSignal?: AbortSignal;
	onVisualReadyWaitStart?: () => void;
};

type MountOptions = {
	revealOnLoad?: boolean;
};

const CHECKOUT_OPEN_HANDSHAKE_TIMEOUT_MS = 12_000;
const CHECKOUT_VISUAL_READY_FALLBACK_MS = 12_000;

/**
 * Flags that are sent back from the Express Checkout iframe when it is closed
 */
type CloseFlags = {
	redirect?: string;
	reload?: boolean;
	refresh?: boolean;
};

function insertCheckoutContainerFragment(containerFragment: string): void {
	const parsedDocument = new DOMParser().parseFromString(containerFragment, 'text/html');
	const fragment = document.createDocumentFragment();

	for (const $script of Array.from(parsedDocument.querySelectorAll('script'))) {
		$script.remove();
	}

	for (const $element of Array.from(parsedDocument.body.querySelectorAll('*'))) {
		for (const attribute of Array.from($element.attributes)) {
			if (attribute.name.toLowerCase().startsWith('on')) {
				$element.removeAttribute(attribute.name);
			}
		}
	}

	for (const node of Array.from(parsedDocument.body.childNodes)) {
		fragment.append(document.importNode(node, true));
	}

	document.head.after(fragment);
}

/**
 *
 */
function expressCheckoutInstance(sdkContext: SDKOptions) {
	insertCheckoutContainerFragment(sdkContext.checkoutContainerFragment);

	const $iframeContainer = document.querySelector<HTMLDivElement>('#peachpay-checkout-container');
	if (!$iframeContainer) {
		throw new Error('Integration error. The Express Checkout container element is missing.');
	}

	const $iframeBackdrop = document.querySelector<HTMLDivElement>('#peachpay-checkout-backdrop');
	if (!$iframeBackdrop) {
		throw new Error('Integration error. The Express Checkout backdrop element is missing.');
	}

	const $loadError = document.querySelector<HTMLDivElement>('#peachpay-checkout-load-error');

	let iframeMountStarted = false;
	let iframeMountPromise: Promise<HTMLIFrameElement> | null = null;
	let $iframe: HTMLIFrameElement | null = null;
	let checkoutOpenRequestId = 0;
	let hideAnimationTimer: ReturnType<typeof setTimeout> | null = null;
	let openAttemptId = 0;
	let cancelledOpenAttemptId = 0;
	let openAttemptAbortController: AbortController | null = null;

	function beginOpenAttempt(): number {
		openAttemptAbortController?.abort();
		openAttemptAbortController = new AbortController();
		openAttemptId++;
		return openAttemptId;
	}

	function cancelOpenAttempt(): void {
		cancelledOpenAttemptId = openAttemptId;
		openAttemptAbortController?.abort();
		openAttemptAbortController = null;
	}

	function getOpenAttemptAbortSignal(attemptId: number): AbortSignal | null {
		if (!isOpenAttemptActive(attemptId)) {
			return null;
		}

		return openAttemptAbortController?.signal ?? null;
	}

	function isOpenAttemptActive(attemptId: number): boolean {
		return attemptId === openAttemptId && attemptId > cancelledOpenAttemptId;
	}

	function clearPendingHideAnimation(): void {
		if (hideAnimationTimer) {
			clearTimeout(hideAnimationTimer);
			hideAnimationTimer = null;
		}

		$iframeBackdrop?.classList?.remove('out');
		$iframe?.classList.remove('out');
	}

	function showShell(): number {
		const currentOpenAttemptId = beginOpenAttempt();
		clearPendingHideAnimation();
		hideCheckoutLoadError();
		lockDocumentBody();
		$iframeBackdrop!.style.display = 'flex';
		showCheckoutSkeleton({onCancel: checkout.hide});

		return currentOpenAttemptId;
	}

	function targetOrigin(): string {
		return location.origin === 'null' ? '*' : location.origin;
	}

	function revealIframe(): void {
		hideCheckoutSkeleton();
		hideCheckoutLoadError();
		$iframe?.classList.remove('hide');
		$iframe?.contentWindow?.focus();
	}

	function hideCheckoutLoadError(): void {
		$loadError?.classList.add('hide');
	}

	function showCheckoutLoadError(): void {
		hideCheckoutSkeleton();
		lockDocumentBody();
		$iframeBackdrop!.style.display = 'flex';
		$iframeBackdrop!.classList.remove('out');
		$iframe?.classList.add('hide');
		$loadError?.classList.remove('hide');
		$loadError?.querySelector<HTMLElement>('.peachpay-checkout-load-error-checkout-link')?.focus();
	}

	$loadError?.querySelector<HTMLButtonElement>('.peachpay-checkout-load-error-close')?.addEventListener('click', () => {
		checkout.hide();
	});

	async function notifyIframeOpened(options: CheckoutOpenContext = {}): Promise<void> {
		if (!$iframe?.contentWindow || options.abortSignal?.aborted) {
			return;
		}

		const requestId = ++checkoutOpenRequestId;
		const useEmbeddedCartCalculationResponse = sdkContext.checkoutEmbedsCartCalculationResponse === true && options.needsCartRefresh !== true && options.iframeMountedForCurrentOpen === true;
		const notifyOpened = () => {
			if (options.abortSignal?.aborted) {
				return;
			}

			$iframe?.contentWindow?.postMessage({type: 'peachpay_checkout_opened', requestId, useEmbeddedCartCalculationResponse}, targetOrigin());
		};

		await new Promise<void>((resolve, reject) => {
			const retryTimer = setInterval(notifyOpened, 500);
			const handshakeTimeoutTimer = setTimeout(() => {
				done();
				reject(new Error('PeachPay checkout iframe did not acknowledge open request within 12000ms.'));
			}, CHECKOUT_OPEN_HANDSHAKE_TIMEOUT_MS);
			let visualReadyTimeoutTimer: ReturnType<typeof setTimeout> | null = null;

			function done() {
				clearInterval(retryTimer);
				clearTimeout(handshakeTimeoutTimer);

				if (visualReadyTimeoutTimer) {
					clearTimeout(visualReadyTimeoutTimer);
				}

				window.removeEventListener('message', handleMessage);
				options.abortSignal?.removeEventListener('abort', handleAbort);
			}

			function startVisualReadyTimeout() {
				if (visualReadyTimeoutTimer) {
					return;
				}

				visualReadyTimeoutTimer = setTimeout(() => {
					done();
					resolve();
				}, CHECKOUT_VISUAL_READY_FALLBACK_MS);
			}

			function handleAbort() {
				done();
				resolve();
			}

			function handleMessage(e: MessageEvent) {
				if (e.origin !== location.origin) {
					return;
				}

				if (e.data.type === 'peachpay_checkout_opened_ready' && e.data.requestId === requestId) {
					clearInterval(retryTimer);
					clearTimeout(handshakeTimeoutTimer);

					if (e.data.visualReadySupported === true) {
						options.onVisualReadyWaitStart?.();
						startVisualReadyTimeout();
						return;
					}

					done();
					resolve();
					return;
				}

				if (e.data.type === 'peachpay_checkout_visual_ready' && e.data.requestId === requestId) {
					done();
					resolve();
				}
			}

			window.addEventListener('message', handleMessage);
			options.abortSignal?.addEventListener('abort', handleAbort, {once: true});
			notifyOpened();
		});
	}

	const checkout = {
		/**
		 * Shows the PeachPay Checkout
		 */
		show(): number {
			try {
				return showShell();
			} catch (e) {
				sdkContext.errorReporter(e);
				return 0;
			}
		},
		/**
		 * Starts loading the PeachPay Checkout iframe without showing the shell or notifying the iframe that checkout opened.
		 */
		preload(): void {
			void checkout.maybeMount({revealOnLoad: false}).catch(sdkContext.errorReporter);
		},
		async open(options: CheckoutOpenOptions = {}): Promise<void> {
			const currentOpenAttemptId = options.openAttemptId ?? checkout.show();

			if (!isOpenAttemptActive(currentOpenAttemptId)) {
				return;
			}

			try {
				const iframeMountedForCurrentOpen = !iframeMountStarted && !iframeMountPromise && !$iframe;
				await checkout.maybeMount({revealOnLoad: false});

				if (!isOpenAttemptActive(currentOpenAttemptId)) {
					return;
				}

				const abortSignal = getOpenAttemptAbortSignal(currentOpenAttemptId);

				if (!abortSignal) {
					return;
				}

				await notifyIframeOpened({
					...options,
					iframeMountedForCurrentOpen,
					abortSignal,
					onVisualReadyWaitStart() {
						if (isOpenAttemptActive(currentOpenAttemptId)) {
							revealIframe();
						}
					},
				});

				if (!isOpenAttemptActive(currentOpenAttemptId)) {
					return;
				}

				revealIframe();
			} catch (e) {
				if (isOpenAttemptActive(currentOpenAttemptId)) {
					showCheckoutLoadError();
				}

				sdkContext.errorReporter(e);
			}
		},
		/**
		 * Hides the PeachPay Checkout
		 */
		hide(): void {
			try {
				cancelOpenAttempt();
				hideCheckoutSkeleton();
				hideCheckoutLoadError();
				unlockDocumentBody();

				$iframe?.contentWindow?.blur();
				$iframe?.classList.add('out');

				$iframeBackdrop?.classList?.add('out');

				if (hideAnimationTimer) {
					clearTimeout(hideAnimationTimer);
				}

				hideAnimationTimer = setTimeout(() => {
					$iframeBackdrop.style.display = 'none';
					$iframeBackdrop?.classList?.remove('out');

					$iframe?.classList.add('hide');
					$iframe?.classList.remove('out');
					hideAnimationTimer = null;
				}, 500);

				$iframe?.contentWindow?.postMessage({type: 'peachpay_checkout_closed'}, targetOrigin());
			} catch (e) {
				sdkContext.errorReporter(e);
			}
		},
		/**
		 * Mounts the PeachPay Checkout iFrame DOM element if it has not already been mounted. Waits for the iFrame to load before resolving.
		 */
		async maybeMount(options: MountOptions = {}): Promise<void> {
			if ($iframe) {
				if (options.revealOnLoad) {
					revealIframe();
				}

				return;
			}

			if (iframeMountPromise) {
				$iframe = await iframeMountPromise;

				if (options.revealOnLoad) {
					revealIframe();
				}

				return;
			}

			if (iframeMountStarted) {
				return;
			}

			iframeMountStarted = true;
			iframeMountPromise = new Promise((resolve, reject) => {
				const $iframe = document.createElement('iframe');
				$iframe.setAttribute('id', 'peachpay-checkout-iframe');
				$iframe.setAttribute('title', 'PeachPay Checkout');
				$iframe.setAttribute('class', 'hide');

				$iframe.onload = () => {
					resolve($iframe);
				};

				$iframe.onerror = e => {
					iframeMountStarted = false;
					iframeMountPromise = null;
					$iframe.remove();
					reject(e);
				};

				$iframe.src = sdkContext.checkoutURL;
				$iframeBackdrop.insertAdjacentElement('beforeend', $iframe);
			});
			$iframe = await iframeMountPromise;

			if (options.revealOnLoad) {
				revealIframe();
			}

			window.addEventListener('message', e => {
				if (e.origin !== location.origin) {
					return;
				}

				if (e.data.type === 'peachpay_checkout_close') {
					checkout.hide();
				}
			});
		},
		isOpenAttemptActive(openAttemptId: number): boolean {
			return isOpenAttemptActive(openAttemptId);
		},
		/**
		 * Registers a callback that is called when the PeachPay Checkout is closed
		 */
		onClose(callback: (event: CloseFlags) => void): void {
			window.addEventListener('message', e => {
				if (e.origin !== location.origin) {
					return;
				}

				if (e.data.type === 'peachpay_checkout_close_flags') {
					const data = e.data.data as CloseFlags;
					callback(data);
				}
			});
		},
	} satisfies ExpressCheckoutAPI;

	document.addEventListener('DOMContentLoaded', () => {
		// Move the iframe container to the end of the body so we have the
		// highest z-index priority
		document.body.appendChild($iframeContainer);
	});

	return checkout;
}

const handleTouchMove = (e: Event) => {
	e.preventDefault();
};

function lockDocumentBody() {
	// We get the scrollbar width because when the `.peachpay-lock-body` class is applied to the body it
	// adds `overflow:hidden` which causes the scrollbar to be removed. To prevent layout shifts we add
	// a right padding to the body to equal the scrollbar width.
	const bodyScrollBarWidth = window.innerWidth - document.documentElement.clientWidth;
	document.body.style.setProperty('--peachpay-scrollbar-width', `${bodyScrollBarWidth}px`);
	document.body.classList.add('peachpay-lock-body');
	document.body.addEventListener('touchmove', handleTouchMove, false);
}

function unlockDocumentBody() {
	document.body.classList.remove('peachpay-lock-body');
	document.body.removeEventListener('touchmove', handleTouchMove, false);
}

export {
	type CloseFlags,
	type CheckoutOpenOptions,
	expressCheckoutInstance,
};
